import { t } from "@travily/i18n";
import { Wizard } from "@ui/components/Wizard";
import type { ReactElement } from "react";
import { useIntercom } from "@/hooks/use-intercom";

interface CreatorTopBarActionsOptions {
  /** Product/Service: client-side navigate to this URL ("Finish later"). */
  saveHref?: string;
  /** Account/Founding-Member: the wizard's existing pause handler (POSTs the
   *  current step). Pass `onPause` straight through — `undefined` (e.g. a step
   *  with no pause route) omits the button. */
  onPause?: () => void;
  /** Entry pages: nothing to save yet, so the label promises only "Finish
   *  later" while the handler posts the skip/defer action. */
  onFinishLater?: () => void;
}

/**
 * Standard creator-wizard top-bar tertiary actions: a "Questions?" button that
 * opens the Intercom messenger, optionally followed by "Save and finish later".
 *
 * Returns raw `Wizard.TertiaryButton` elements (not a wrapping component) so the
 * caller spreads them directly inside `<Wizard>` — `useWizardSlots` walks children
 * with `Children.forEach`, which flattens the array and matches each button by its
 * slot type. Questions is first so it renders to the left of Save.
 */
export function useCreatorTopBarActions({
  saveHref,
  onPause,
  onFinishLater,
}: CreatorTopBarActionsOptions = {}): ReactElement[] {
  const { openMessenger } = useIntercom();

  const actions: ReactElement[] = [
    <Wizard.TertiaryButton key="questions" onPress={openMessenger}>
      {t("common.actions.questions")}
    </Wizard.TertiaryButton>,
  ];

  // Product/Service navigate away without flushing the current step (the draft
  // already persists per completed step), so the label promises only "Finish
  // later" — not "Save". Account/FM POST the current step via `onPause`, so they
  // keep the "Save and finish later" wording.
  if (saveHref) {
    actions.push(
      <Wizard.TertiaryButton key="save" href={saveHref}>
        {t("common.actions.finish_later")}
      </Wizard.TertiaryButton>,
    );
  } else if (onFinishLater) {
    actions.push(
      <Wizard.TertiaryButton key="save" onPress={onFinishLater}>
        {t("common.actions.finish_later")}
      </Wizard.TertiaryButton>,
    );
  } else if (onPause) {
    actions.push(
      <Wizard.TertiaryButton key="save" mobileLabel={t("common.actions.save_and_exit")} onPress={onPause}>
        {t("common.actions.save_and_finish_later")}
      </Wizard.TertiaryButton>,
    );
  }

  return actions;
}
